import { __awaiter } from '../_virtual/_tslib.js';
import { useCallback, useContext, useState, useEffect } from 'react';
import { AppContext } from '../app-context.js';
import { searchUsingPlacesAPI } from '../_services/city-search.service.js';
import { STATE_LIST } from '../constants/StateList.js';
import { getCurrentLocation } from '../methods/getCurrentLocation.js';
import { useCsDashboard } from './useCsDashboard.js';

function useSearch({ defaultOptions, onSelectItem, showCurrentLocationOption, locationParamsOption }) {
    const [searchText, setSearchText] = useState("");
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [_defaultOptions, setDefaultOptions] = useState();
    const [searchOptions, setSearchOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState();
    const { isCsDashboardUser } = useCsDashboard();
    const onInputChange = useCallback((input) => {
        input === "" && setSearchOptions(_defaultOptions || []);
    }, [defaultOptions]);
    const onFocus = () => {
        setSearchOptions(_defaultOptions || []);
    };
    const _onItemSelected = (selectedItems) => {
        var _a;
        if (((_a = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems[0]) === null || _a === void 0 ? void 0 : _a.category) === "currentLoc") {
            setSelectedItem(undefined);
        }
        else {
            setSelectedItem(selectedItems[0]);
        }
        onSelectItem === null || onSelectItem === void 0 ? void 0 : onSelectItem(selectedItems);
    };
    useEffect(() => {
        if (showCurrentLocationOption && !isCsDashboardUser) {
            setDefaultOptions([{ name: "Use Current City", category: "currentLoc" }, ...(defaultOptions || [])]);
        }
        else {
            setDefaultOptions(defaultOptions);
        }
    }, [showCurrentLocationOption, defaultOptions]);
    useEffect(() => {
        _defaultOptions && setSearchOptions(_defaultOptions);
    }, [_defaultOptions]);
    return {
        searchText,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}
function useStateSearch({ defaultOptions, onSelectItem, showCurrentLocationOption, locationParamsOption }) {
    const { isSearchLoading, onInputChange, searchOptions, searchText, setIsSearchLoading, setSearchText, setSearchOptions, _onItemSelected, selectedItem, setSelectedItem, onFocus } = useSearch({
        locationParamsOption,
        defaultOptions,
        onSelectItem,
        showCurrentLocationOption
    });
    const labelKey = ({ state }) => state || "";
    const onSearchInput = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        query = decodeURIComponent(query);
        setIsSearchLoading(true);
        setSearchText(query);
        const options = STATE_LIST.filter((s) => s.name.match(new RegExp("^" + query, "i")));
        setSearchOptions(options);
        setIsSearchLoading(false);
    }), []);
    return {
        searchText,
        labelKey,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        onSearchInput,
        onItemSelected: _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}
function usePlaceSearch({ defaultOptions, onSelectItem, showCurrentLocationOption, locationParamsOption }) {
    const { updateRecentTransporterCitySearches } = useContext(AppContext);
    const { isSearchLoading, onInputChange, searchOptions, searchText, setIsSearchLoading, setSearchText, setSearchOptions, _onItemSelected, selectedItem, setSelectedItem, onFocus } = useSearch({
        defaultOptions,
        onSelectItem,
        showCurrentLocationOption,
        locationParamsOption
    });
    const labelKey = ({ name }) => {
        return name ? name : "";
    };
    const onSearchInput = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        setIsSearchLoading(true);
        setSearchText(query);
        try {
            const options = (yield searchUsingPlacesAPI(query, locationParamsOption));
            if (options.length !== 0)
                setSearchOptions(options.map((option, i) => {
                    return {
                        id: i,
                        name: option.description,
                        state_id: i,
                        state_code: "",
                        state: option.state,
                        place_id: option.place_id,
                        city: option.city
                    };
                }));
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setIsSearchLoading(false);
        }
    }), []);
    const __onItemSelected = (selectedItems) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (((_a = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems[0]) === null || _a === void 0 ? void 0 : _a.category) === "currentLoc") {
            setIsSearchLoading(true);
            const currLoc = yield getCurrentLocation(true, locationParamsOption);
            if (!currLoc || currLoc.status === "failed") {
                setIsSearchLoading(false);
                return;
            }
            _onItemSelected === null || _onItemSelected === void 0 ? void 0 : _onItemSelected([currLoc]);
            setIsSearchLoading(false);
            return;
        }
        if (selectedItems[0])
            updateRecentTransporterCitySearches === null || updateRecentTransporterCitySearches === void 0 ? void 0 : updateRecentTransporterCitySearches(selectedItems[0]);
        _onItemSelected === null || _onItemSelected === void 0 ? void 0 : _onItemSelected(selectedItems);
        return;
    });
    return {
        labelKey,
        searchText,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        onSearchInput,
        onItemSelected: __onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}

export { usePlaceSearch, useStateSearch };
//# sourceMappingURL=useSearch.js.map
